import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Empty,
  notification,
  Pagination,
  Result,
  Spin,
  Tag,
} from "antd";
import useFormatApiRequest from "../../hooks/formatApiRequest";
import { useAppDispatch } from "../../Redux/reduxCustomHook";
import "./User-Profile-Comp.css";
import { DollarOutlined } from "@ant-design/icons";
import { formatCurrency } from "../../utils/basic.utils";
import { formatDateTime } from "../../utils/date.utils";
import {
  userGetInvoice,
  userGetTransactions,
} from "../../apiservice/userService";
import {
  IinvoiceData,
  IUserTransactionsTypeData,
} from "../../apiservice/userService.type";
import { ILoadState } from "../../utils/loading.utils.";

type NotificationType = "success" | "info" | "warning" | "error";

type IUserTransactionsList = {
  externalFilter?: any;
  initialDefaultFilter?: any;
  hidePagination?: boolean;
};

export const UserTransactionsList: React.FC<IUserTransactionsList> = ({
  externalFilter,
  initialDefaultFilter,
  hidePagination = false,
}) => {
  const [transactionsLoadState, setTransactionsLoadState] =
    useState<ILoadState>("loading");
  const [loadTransactionsData, setLoadtransactionsData] = useState(true);
  const [transactionsDefaultFilter, setTransactionsDefaultFilter] = useState(
    initialDefaultFilter || { page: 1, pageSize: 10, statusId: 1 }
  );
  const [tableData, setTableData] = useState<IinvoiceData[]>([]);
  const [api, contextHolder] = notification.useNotification();

  // Pagination Constants
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(1);
  const perPage = 10;

  // Redux and navigation hooks
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Update transactions default filter when external filter changes
  useEffect(() => {
    if (externalFilter) {
      setTransactionsDefaultFilter({
        ...transactionsDefaultFilter,
        ...externalFilter,
      });
      setLoadtransactionsData(true);
      setTransactionsLoadState("loading");
    }
  }, [externalFilter]);

  // Custom hook to load all transaction details
  const transactionsDataResult = useFormatApiRequest(
    () => userGetTransactions(transactionsDefaultFilter),
    loadTransactionsData,
    () => {
      setLoadtransactionsData(false);
    },
    () => {
      processtransactionsResult();
    }
  );

  // Process transaction results
  const processtransactionsResult = async () => {
    if (transactionsDataResult.httpState === "SUCCESS") {
      setTableData(transactionsDataResult.data?.data || []);
      setTransactionsLoadState("completed");
      setTotalItems(transactionsDataResult.data?.meta?.total || 10);
      if (!transactionsDataResult.data?.meta?.total) {
        setTransactionsLoadState("noData");
      }
    } else if (transactionsDataResult.httpState === "ERROR") {
      setTransactionsLoadState("error");
    } else if (transactionsDataResult.httpState === "LOADING") {
      setTransactionsLoadState("loading");
    }
  };

  // Show notification
  const openNotificationWithIcon = (
    type: NotificationType,
    message: string,
    description: string,
    background?: string
  ) => {
    api[type]({
      message,
      description,
      placement: "bottomRight",
      style: { background },
    });
  };

  // Handle pagination change
  const onPageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setTransactionsDefaultFilter({
      ...transactionsDefaultFilter,
      page: page,
      perPage: pageSize,
    });
    setLoadtransactionsData(true);
  };

  // Navigate to detailed transaction page
  const navigateForward = async (id: number) => {
    console.log(id);
    navigate(`/users/transactions/${id}`);
  };

  return (
    <>
      {contextHolder}
      <div className="grid pt-4">
        {transactionsLoadState === "loading" && (
          <div className="mt-2 mb-2 flex justify-center items-center pt-20 pb-20">
            <Spin size="large" />
          </div>
        )}

        {transactionsLoadState === "error" && (
          <div className="mt-2 mb-2">
            <Result
              status="500"
              title={
                <span className="text-gray-900 dark:text-stone-100">Error</span>
              }
              subTitle={
                <span className="text-gray-600 dark:text-stone-400">
                  Sorry, something went wrong, it could be a network-related
                  error
                </span>
              }
              extra={
                <Button
                  onClick={() => setLoadtransactionsData(true)}
                  type="primary"
                >
                  Reload
                </Button>
              }
            />
          </div>
        )}

        {transactionsLoadState === "noData" && (
          <div className="mt-8">
            <div className="flex justify-center mb-6">
              <span className="fontKanitRegular font-medium text-xl text-center text-gray-900 dark:text-stone-100">
                No Transactions Available
              </span>
            </div>
            <div className="flex justify-center items-center">
              <Empty
                image={
                  <DollarOutlined
                    style={{ fontSize: 48 }}
                    className="text-gray-300 dark:text-stone-600"
                  />
                }
                description={
                  <span className="fontAlbertSansRegular text-center text-gray-600 dark:text-stone-400">
                    Once you complete transactions, they will appear here.
                  </span>
                }
              />
            </div>
          </div>
        )}

        {transactionsLoadState === "completed" && (
          <div className="mt-2">
            <div className="grid grid-cols-1 gap-4">
              {tableData.map((transaction) => (
                <div
                  key={transaction.id}
                  // onClick={() => navigateForward(transaction.id)}
                  // className="transaction-card cursor-pointer" // Added cursor-pointer
                >
                  <div className="flex flex-row p-2 w-full bg-white dark:bg-stone-800 border border-gray-200 dark:border-stone-700 rounded-xl hover:shadow-md dark:hover:bg-stone-700">
                    <div className="grow p-2 flex flex-col">
                      <h2 className="font-semibold text-md mb-2 text-gray-900 dark:text-stone-100">
                        {transaction.title}{" "}
                        {formatCurrency(transaction.amount)}
                      </h2>
                      <p className="text-sm text-gray-700 dark:text-stone-300">
                        {formatDateTime(transaction.dateCreated)}
                      </p>

                      {/* Tags Row */}
                      <div className="flex flex-row gap-2 mt-2">
                        <Tag
                          color={transaction.statusId === 1 ? "green" : "red"}
                        >
                          {transaction.statusName}
                        </Tag>
                      </div>
                    </div>

                    <div className="flex items-center justify-end pr-2">
                      <p
                        className="text-sm font-san"
                        style={{
                          color: transaction.amount > 0 ? "green" : "red",
                        }}
                      >
                        {formatCurrency(transaction.amount)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {!hidePagination && (
              <div className="grid mt-6">
                <Pagination
                  current={currentPage || 1}
                  onChange={onPageChange}
                  pageSize={perPage}
                  total={totalItems}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};
